import { useEffect, useState } from 'react';
import { api } from '../../lib/api.js';
import { useUnsavedChanges } from '../../lib/use-unsaved-changes.js';
import { ALL_COLLECTOR_IDS, TIER_COLLECTORS } from '../../../../shared/collectors.mjs';
import { PERMISSION_GROUPS } from '../../../../shared/permission-map.mjs';
import { DEFAULT_SCHEDULE, DEFAULT_RETENTION_DAYS, validateSchedule, validateRetentionDays } from '../../../../shared/schedule.mjs';

// The Data collection form (ENTRAID-347 redesign, plan C5): which sources to collect, the schedule and the retention.
// One form for the page, so one unsaved-changes guard covers Sources and Schedule and retention together. Moved from
// the retired CustomerSettings page with its behavior unchanged (ENTRAID-269, 312, 350).

const formSnap = (set, cron, days) => JSON.stringify([[...set].sort(), cron, String(days)]);

// ENTRAID-350: every source ticked is "All data sources" (one consent for all 17 permissions); anything less is a custom
// selection, and the customer's own Entra app then declares exactly the permissions those sources need (Core always).
export const selectionFor = (set) => (set.size >= ALL_COLLECTOR_IDS.length
  ? { mode: 'all' }
  : { mode: 'custom', sources: [...set].sort() });

// A permission in plain words: its Microsoft name and the area it serves, e.g. "Sites.Read.All (Microsoft 365 usage)".
const areaOf = (permission) => PERMISSION_GROUPS.find((g) => g.permissions.includes(permission))?.title;
const plainPermission = (permission) => (areaOf(permission) ? `${permission} (${areaOf(permission)})` : permission);

/** The message after a save, from the permissions answer (ENTRAID-350 spec 7.3). */
export function permissionsNotice(res, error) {
  if (error) {
    const text = typeof error === 'string' ? error : error.message;
    const reason = typeof error === 'string' ? null : error.reason;
    // Review of PR 425 (M1, M2): the backend's own words when the app could not be put back, or another save won the race;
    // never "Nothing changed there" for those.
    if (reason === 'manifest-revert-failed' || reason === 'selection-changed' || reason === 'consent-in-progress') return { kind: 'warn', text };
    return { kind: 'warn', text: `Saved, but the permissions of your Microsoft Entra app could not be updated (${text}). Nothing changed there; try saving again.` };
  }
  if (res?.removalGuide) {
    return { kind: 'warn', text: `Saved. Microsoft keeps a permission you already granted until you remove it. Still granted: ${res.removalGuide.permissions.join(', ')}. ${res.removalGuide.steps.join(' ')}` };
  }
  // ENTRAID-353 follow-up: the Entra side is always stated, never silence. consentState comes from the permissions answer;
  // an answer without it (older backend) falls back to consentRequired alone.
  const needed = res?.added?.length ? res.added : (res?.missingPermissions ?? []);
  if (res?.consentRequired || res?.consentState === 'required') {
    const list = needed.map(plainPermission).join(', ');
    return { kind: 'warn', text: `Saved. The new sources need ${needed.length} more read-only permission${needed.length === 1 ? '' : 's'} in Microsoft Entra${list ? ` (${list})` : ''}. A Global Administrator approves all of them in one consent, with the admin consent link under "Needs admin consent" at the top of this page.` };
  }
  if (res?.consentState === 'none-needed') {
    return { kind: 'ok', text: 'Saved. No new Microsoft Entra consent is needed: the new sources are collected on the next run, or now with Collect now.' };
  }
  if (res?.consentState === 'unknown') {
    return { kind: 'warn', text: 'Saved. The portal cannot confirm whether Microsoft Entra consent covers the new sources for this customer. If a source returns no data after the next run, contact IPC Global support to check the consent.' };
  }
  return null;
}

/**
 * @param {{ customerId: string, tier?: string, config?: object|null, onSave?: () => void }} props
 */
export function useCollectionSettings({ customerId, tier, config: initialConfig, onSave }) {
  const [enabled, setEnabled] = useState(new Set());
  const [scheduleCron, setScheduleCron] = useState(DEFAULT_SCHEDULE);
  const [retentionDays, setRetentionDays] = useState(DEFAULT_RETENTION_DAYS);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [msgSource, setMsgSource] = useState(null); // 'datasources' | 'schedule'
  const [loaded, setLoaded] = useState(false);
  // Per-source hints (path pattern and cadence) from the authenticated data reference; the catalog itself is never
  // bundled into the SPA.
  const [hints, setHints] = useState({});
  // Last loaded or saved values; the form is unsaved while it differs from them.
  const [savedSnap, setSavedSnap] = useState(null);
  const snap = formSnap(enabled, scheduleCron, retentionDays);
  const dirty = loaded && savedSnap !== null && snap !== savedSnap;
  // The sources part alone, for the sticky Save bar of the Sources card.
  const sourcesDirty = dirty && savedSnap !== null && JSON.parse(savedSnap)[0].join('|') !== [...enabled].sort().join('|');
  useUnsavedChanges(dirty);

  useEffect(() => {
    if (!customerId) return undefined;
    let cancelled = false;
    api.getDataReference(customerId)
      .then((res) => {
        if (cancelled) return;
        const next = {};
        for (const cat of res?.reference?.categories ?? []) {
          for (const src of cat.sources) next[src.key] = src.hint;
        }
        setHints(next);
      })
      // Hints are an aid, not a requirement: the toggles work without them.
      .catch(() => {});
    return () => { cancelled = true; };
  }, [customerId]);

  useEffect(() => {
    if (initialConfig) {
      const set = new Set(initialConfig.enabledCollectors ?? []);
      const cron = initialConfig.scheduleCron ?? DEFAULT_SCHEDULE;
      const days = initialConfig.dataRetentionDays ?? DEFAULT_RETENTION_DAYS;
      setEnabled(set);
      setScheduleCron(cron);
      setRetentionDays(days);
      setSavedSnap(formSnap(set, cron, days));
    } else {
      const defaults = TIER_COLLECTORS[tier] ?? TIER_COLLECTORS.starter;
      setEnabled(new Set(defaults));
      setSavedSnap(formSnap(new Set(defaults), DEFAULT_SCHEDULE, DEFAULT_RETENTION_DAYS));
    }
    setLoaded(true);
  }, [initialConfig, tier]);

  function toggle(id) {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleCategory(cat) {
    setEnabled((prev) => {
      const next = new Set(prev);
      const ids = cat.collectors.map((c) => c.id);
      const allOn = ids.every((id) => next.has(id));
      ids.forEach((id) => (allOn ? next.delete(id) : next.add(id)));
      return next;
    });
  }

  // ENTRAID-312: sources the catalog gained since the admin last saved.
  const newSources = initialConfig?.availableNotEnabled ?? [];
  const newFree = newSources.filter((s) => !s.requires);
  const newLicensed = newSources.filter((s) => s.requires);
  const freePending = newFree.filter((s) => !enabled.has(s.id));

  // Ticks the new sources that need no license; the license-gated ones stay the admin's own choice. Saving is still
  // the admin's step.
  function enableAllNew() {
    setEnabled((prev) => new Set([...prev, ...newFree.map((s) => s.id)]));
  }

  /** Replaces the selection (Default set or Select all, SourcesCard SelectionShortcuts). Saving stays the admin's step. */
  function setSources(ids) {
    setEnabled(new Set(ids));
  }

  function revertSources() {
    if (savedSnap === null) return;
    setEnabled(new Set(JSON.parse(savedSnap)[0]));
  }

  async function save(source) {
    setMsg(null); setMsgSource(source);
    const scheduleError = validateSchedule(scheduleCron);
    const retentionError = validateRetentionDays(retentionDays);
    if (scheduleError || retentionError) {
      setMsgSource('schedule');
      setMsg({ kind: 'err', text: scheduleError ? `Collection schedule: ${scheduleError}.` : `Data retention: ${retentionError}.` });
      return false;
    }
    setBusy(true);
    try {
      const res = await api.updateConfig(customerId, {
        enabledCollectors: Array.from(enabled),
        scheduleCron,
        dataRetentionDays: parseInt(retentionDays, 10),
        // The alert email lives on the Recipients tab of Notifications (NotificationRecipients.jsx), its only home:
        // never sent from here, so a save here cannot clear it.
      });
      // ENTRAID-350: the sources now decide what the customer's own Entra app may read. Only the Sources save does it.
      let permissionsAnswer = null;
      let permissionsError = null;
      if (source === 'datasources') {
        try {
          permissionsAnswer = await api.updatePermissions(customerId, selectionFor(enabled));
        } catch (e) {
          permissionsError = { message: e.message, reason: typeof e?.body?.reason === 'string' ? e.body.reason : null };
        }
      }
      const permissionsMsg = source === 'datasources' ? permissionsNotice(permissionsAnswer, permissionsError) : null;
      if (permissionsMsg) {
        setMsg(permissionsMsg);
      } else if (res?.propagatedToCustomerAccount === false) {
        setMsg({
          kind: 'warn',
          text: 'Saved in the portal, but the change could not be synced to the customer account yet. It will apply on the next deploy, or contact IPC Global support.',
        });
      } else if (res?.stackSettings?.state === 'failed') {
        setMsg({
          kind: 'warn',
          text: `Saved, but the schedule and retention could not be applied to your AWS account yet (${res.stackSettings.error}). They are applied again on the next collector update.`,
        });
      } else if (res?.stackSettings?.cfnAction === 'update') {
        setMsg({ kind: 'ok', text: 'Settings saved. The new schedule and retention are being applied to your AWS account (about a minute).' });
      } else {
        setMsg({ kind: 'ok', text: 'Settings saved. Changes take effect on the next scheduled run.' });
      }
      setSavedSnap(formSnap(enabled, scheduleCron, retentionDays));
      onSave?.();
      return true;
    } catch (e) {
      setMsg({ kind: 'err', text: e.message });
      return false;
    } finally {
      setBusy(false);
    }
  }

  return {
    enabled, toggle, toggleCategory, scheduleCron, setScheduleCron, retentionDays, setRetentionDays,
    busy, msg, msgSource, loaded, hints, dirty, sourcesDirty, save, revertSources, setSources,
    newSources, newFree, newLicensed, freePending, enableAllNew,
    licenseDisabled: initialConfig?.licenseDisabled ?? {},
  };
}
